import {
  ENABLE_MODULE_MASTER_STORAGE_KEY,
  MODULE_ID_STORAGE_KEY,
} from 'constants/common.const';
import { MODULE_ID, PHOTO_PAGE } from 'constants/photo.const';
import { includesId } from 'helpers/array.helper';
import { getLocalStorage } from 'helpers/common.helper';
import { isEqualVal } from 'helpers/string.helper';
import { isEqualPath } from 'helpers/url.helper';
import { find, findKey, includes, size } from 'lodash';
import { useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useAppSelector } from 'store/store-hooks';

// Các page không có trong luồng event booth
const PageWillSkipWhenBoothEvent = [
  PHOTO_PAGE.FRAME,
  PHOTO_PAGE.PAYMENT_METHOD,
  PHOTO_PAGE.PAYMENT,
  PHOTO_PAGE.QR_CODE,
];

// Các page không có trong luồng tắt thanh toán
const PageWillSkipWhenTurnOffPayment = [
  PHOTO_PAGE.PAYMENT_METHOD,
  PHOTO_PAGE.PAYMENT,
  PHOTO_PAGE.QR_CODE,
];

// Các page không có trong luồng tắt quét mã QR
const PageWillSkipWhenTurnOffQRBanking = [
  PHOTO_PAGE.PAYMENT_METHOD,
  PHOTO_PAGE.QR_CODE,
];

export const useNewPhotoLifeCycleStep = () => {
  const { pathname } = useLocation();
  const { isChoosePaymentMethod, isChooseQRCodeToPay } = useAppSelector(
    (state) => state.selfeApp,
  );
  const {
    LifeCycleStep,
    PrintingLifeCycleStep,
    boothInfo,
    isTurnOnPayment,
    isTurnOnMomoMethodPayment,
    isTurnOnVNPayMethodPayment,
    isThemeBeforePayment,
    loadingInitial,
    isEventBooth,
    isShowQrTransactionImage,
    isRobotArm,
    isOnFeedback,
  } = useAppSelector((s) => s.booth);

  // Check if booth info is properly loaded
  const isBoothInfoLoaded = useMemo(
    () => !!boothInfo && !loadingInitial,
    [boothInfo, loadingInitial],
  );

  // Get the active lifecycle step (handle printing module override)
  const getLifeCycleStep = useCallback(() => {
    const selectedModuleId = getLocalStorage(MODULE_ID_STORAGE_KEY);
    const isEnabledModuleMaster = getLocalStorage(
      ENABLE_MODULE_MASTER_STORAGE_KEY,
    );

    if (
      isEnabledModuleMaster &&
      isEqualVal(selectedModuleId, MODULE_ID.PRINTING_BOOTH)
    ) {
      return PrintingLifeCycleStep;
    }
    return LifeCycleStep;
  }, [LifeCycleStep, PrintingLifeCycleStep]);

  const getCurrentRouteByRouteId = useCallback(
    (routeId: string = '') => {
      if (routeId) {
        return getLifeCycleStep()?.[routeId];
      }
      return null;
    },
    [getLifeCycleStep],
  );

  const getCurrentRouteByPathName = useCallback(
    (path: string = '') => {
      const activeLifeCycle = getLifeCycleStep();
      if (!activeLifeCycle) return null;

      const routeId = findKey(activeLifeCycle, (o) =>
        isEqualPath(o?.route, path || pathname),
      );
      if (routeId) {
        return activeLifeCycle[routeId];
      }
      return null;
    },
    [pathname, getLifeCycleStep],
  );

  const getStartNodePath = useCallback(() => {
    const activeLifeCycle = getLifeCycleStep();
    if (!activeLifeCycle) return '';
    return find(activeLifeCycle, (o) => !!o?.isStartNode)?.route || '';
  }, [getLifeCycleStep]);

  const getNextPath = useCallback(
    (path: string | null = '', skipRouteIds: string[] = []): string => {
      // Early return if booth info isn't loaded yet
      if (!isBoothInfoLoaded) {
        return '';
      }

      const _pathname = path || pathname;
      const currentRoute = getCurrentRouteByPathName(_pathname);
      const nextRouter = getCurrentRouteByRouteId(currentRoute?.nextId);

      // Nếu route tiếp là Theme nhưng đã bật (Theme trước Payment) => skip sang trang AddFilter
      if (
        isThemeBeforePayment &&
        isEqualVal(currentRoute?.nextId, PHOTO_PAGE.THEME)
      ) {
        return getCurrentRouteByRouteId(PHOTO_PAGE.ADD_FILTER)?.route || '';
      }

      // nếu route tiếp theo phải check điều kiện2
      if (
        // nếu route tiếp theo trong skipRouteIds => skip luôn
        (!!size(skipRouteIds) && includesId(skipRouteIds, nextRouter?.id)) ||
        // Nếu hiển thị theme trước Payment, next là THEME_BEFORE_PAY => skip luôn
        (!isThemeBeforePayment &&
          isEqualVal(nextRouter?.id, PHOTO_PAGE.THEME_BEFORE_PAY)) ||
        // Nếu là booth sự kiện bỏ qua màn chọn frame, thanh toán
        (isEventBooth &&
          includes(PageWillSkipWhenBoothEvent, nextRouter?.id)) ||
        // Nếu đã chọn "Phương thức thanh toán", next là PaymentMethod => skip luôn
        (isChoosePaymentMethod &&
          isEqualVal(nextRouter?.id, PHOTO_PAGE.PAYMENT_METHOD)) ||
        // Nếu bật hiển thị QR ảnh trên booth và không hiển thị feedback => skip màn in và feedback
        (isShowQrTransactionImage &&
          !isOnFeedback &&
          includes([PHOTO_PAGE.PRINT, PHOTO_PAGE.FEEDBACK], nextRouter?.id)) ||
        // -----
        // Nếu không bật hiển thị QR ảnh trên booth lập tức bỏ qua
        (!isShowQrTransactionImage &&
          isEqualVal(nextRouter?.id, PHOTO_PAGE.QR_TRANSACTION_IMAGE)) ||
        // -----
        // Nếu đã chọn phương thức thanh toán là "tiền mặt", next là QR code => skip luôn
        (!isTurnOnPayment &&
          includes(PageWillSkipWhenTurnOffPayment, nextRouter?.id)) ||
        // -----
        // Nếu tắt chức năng thành toán qua momo, chọn phương thức và trang qrcode momo => skip luôn
        (!isTurnOnMomoMethodPayment &&
          !isTurnOnVNPayMethodPayment &&
          includes(PageWillSkipWhenTurnOffQRBanking, nextRouter?.id)) ||
        // -----
        // Nếu đã chọn phương thức thanh toán là "tiền mặt", next là QR code => skip luôn
        (isChoosePaymentMethod &&
          !isChooseQRCodeToPay &&
          isEqualVal(nextRouter?.id, PHOTO_PAGE.QR_CODE)) ||
        // -----
        // Nếu booth không phải Arm Robot Booth, next là route ShootingPosition => skip luôn
        (!isRobotArm &&
          isEqualVal(nextRouter?.id, PHOTO_PAGE.SHOOTING_POSITION))
      ) {
        return getNextPath(nextRouter?.route, skipRouteIds);
      }

      return nextRouter?.route || '';
    },
    [
      isBoothInfoLoaded,
      pathname,
      getCurrentRouteByPathName,
      getCurrentRouteByRouteId,
      isThemeBeforePayment,
      isEventBooth,
      isChoosePaymentMethod,
      isShowQrTransactionImage,
      isOnFeedback,
      isTurnOnPayment,
      isTurnOnMomoMethodPayment,
      isTurnOnVNPayMethodPayment,
      isChooseQRCodeToPay,
      isRobotArm,
    ],
  );

  const getPrevPath = useCallback(
    (path: string | null = '', skipRouteIds: string[] = []): string => {
      // Early return if booth info isn't loaded yet
      if (!isBoothInfoLoaded) {
        return '';
      }

      const _pathname = path || pathname;
      const currentRoute = getCurrentRouteByPathName(_pathname);
      const prevRouter = getCurrentRouteByRouteId(currentRoute?.prevId);

      // nếu route tiếp theo phải check điều kiện
      if (
        // nếu route tiếp theo trong skipRouteIds => skip luôn
        (!!size(skipRouteIds) && includesId(skipRouteIds, prevRouter?.id)) ||
        // Nếu đã chọn "Phương thức thanh toán", prev là PaymentMethod => skip luôn
        (!isThemeBeforePayment &&
          isEqualVal(prevRouter?.id, PHOTO_PAGE.THEME_BEFORE_PAY)) ||
        // Nếu là booth sự kiện bỏ qua màn chọn frame
        (isEventBooth &&
          includes(PageWillSkipWhenBoothEvent, prevRouter?.id)) ||
        // Nếu đã chọn "Phương thức thanh toán", prev là PaymentMethod => skip luôn
        (isChoosePaymentMethod &&
          isEqualVal(prevRouter?.id, PHOTO_PAGE.PAYMENT_METHOD)) ||
        // Nếu không bật hiển thị QR ảnh trên booth lập tức bỏ qua
        (!isShowQrTransactionImage &&
          isEqualVal(prevRouter?.id, PHOTO_PAGE.QR_TRANSACTION_IMAGE)) ||
        // -----
        // Nếu đã chọn phương thức thanh toán là "tiền mặt", next là QR code => skip luôn
        (!isTurnOnPayment &&
          includes(PageWillSkipWhenTurnOffPayment, prevRouter?.id)) ||
        // -----
        // Nếu tắt chức năng thành toán qua momo, chọn phương thức và trang qrcode momo => skip luôn
        (!isTurnOnMomoMethodPayment &&
          !isTurnOnVNPayMethodPayment &&
          includes(PageWillSkipWhenTurnOffQRBanking, prevRouter?.id)) ||
        // -----
        // Nếu đã chọn phương thức thanh toán là "tiền mặt", prev là QR code => skip luôn
        (isChoosePaymentMethod &&
          !isChooseQRCodeToPay &&
          isEqualVal(prevRouter?.id, PHOTO_PAGE.QR_CODE)) ||
        // -----
        // Nếu booth không phải Arm Robot Booth, prev là route ShootingPosition => skip luôn
        (!isRobotArm &&
          isEqualVal(prevRouter?.id, PHOTO_PAGE.SHOOTING_POSITION))
      ) {
        return getPrevPath(prevRouter?.route);
      }

      return prevRouter?.route || '';
    },
    [
      isBoothInfoLoaded,
      pathname,
      getCurrentRouteByPathName,
      getCurrentRouteByRouteId,
      isThemeBeforePayment,
      isEventBooth,
      isChoosePaymentMethod,
      isShowQrTransactionImage,
      isTurnOnPayment,
      isTurnOnMomoMethodPayment,
      isTurnOnVNPayMethodPayment,
      isChooseQRCodeToPay,
      isRobotArm,
    ],
  );

  const currentRoute = useMemo(
    () => getCurrentRouteByPathName(pathname),
    [getCurrentRouteByPathName, pathname],
  );

  return {
    getCurrentRouteByPathName,
    getNextPath,
    getPrevPath,
    getStartNodePath,
    currentRoute,
  };
};
